import { useEffect, useLayoutEffect, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { hasErrorBoundary } from '@xmanscript/has-error-boundary';

interface PortalProps {
  children: ReactNode;
}

/**
 * This is a functional component called `PortalTemplate` that takes in a prop called `children` of
 * type `PortalProps`. It creates two variables `backdropNode` and `portalNode` by getting the elements
 * with ids `backdrop-root` and `overlay-root` respectively.
 * */
function Portal({ children }: PortalProps) {
  const backdropNode = document.getElementById('backdrop-root');
  const portalNode = document.getElementById('overlay-root');

  useLayoutEffect(() => {});

  useEffect(() => {
    const { body } = document;
    body.style.overflow = 'hidden';
    body.style.paddingRight = '17px';

    return () => {
      body.style.overflow = 'auto';
      body.style.paddingRight = '0px';
    };
  }, []);

  return (
    <>
      {backdropNode
        ? createPortal(
            <motion.div
              initial={{ opacity: 0.3 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2, ease: 'easeIn', delay: 0 }}
              className="fixed left-0 top-0 z-50 h-screen w-screen bg-black bg-opacity-50"
            />,
            backdropNode,
          )
        : null}
      {portalNode
        ? createPortal(
            <div className="fixed left-0 top-0 z-50 h-screen w-screen bg-opacity-0">
              <div className="overlay-container relative h-full w-full">{children}</div>
            </div>,
            portalNode,
          )
        : null}
    </>
  );
}

export default hasErrorBoundary(Portal);
